import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import SetupWizardShell from '@devnw/cyber-iam/auth/SetupWizard/SetupWizard'
import type { SetupWizardStep } from '@devnw/cyber-iam/auth/SetupWizard/SetupWizard'
import { createSetupClient } from '../../api/setupClient'
import { Welcome } from './steps/Welcome'
import { AdminAccount } from './steps/AdminAccount'
import { EnrollMFA } from './steps/EnrollMFA'
import { RecoveryKey } from './steps/RecoveryKey'
import { ServiceDefaults } from './steps/ServiceDefaults'
import { Finish } from './steps/Finish'
import { WizardPage } from './setupStyled'

/**
 * SetupWizard — the public, unauthenticated first-run admin-initialization
 * flow (Phase E). Reachable only via the public `/setup` route: `App.tsx`
 * redirects here on first load when `getInitStatus()` reports
 * `!initialized`, and every request this wizard issues goes through
 * `createSetupClient()` (setup-token-gated), never the bearer-token-gated
 * generated admin client — there is no admin session to bear a token yet.
 *
 * Owns exactly one `SetupClient` instance for the lifetime of the wizard so
 * the setup token captured by the Welcome step is available to every later
 * step without threading it through props by hand.
 *
 * OIDCPAM Gate-6 Phase 2b: migrated off MUI's Stepper/Box onto
 * `@devnw/cyber-iam/auth/SetupWizard`'s render-prop step-shell, which fits
 * this wizard's existing linear 6-step flow cleanly — each step's content
 * component keeps its own `onNext`/`onBack` prop shape, wired directly to
 * the shell's per-render `next`/`back` callbacks. The shell owns step-index
 * state and the indicator UI; this page keeps the setup-client instance and
 * the cross-step `serviceName` state exactly as before. The shell's `next()`
 * on the LAST step invokes `onComplete` instead of advancing, so Finish's
 * existing `onComplete` prop (called only after a successful
 * `client.complete()`) is wired straight to the shell's per-step `next`.
 */
export const SetupWizard = (): JSX.Element => {
  const client = useMemo(() => createSetupClient(), [])
  const navigate = useNavigate()
  const [serviceName, setServiceName] = useState('')

  const handleComplete = (): void => {
    navigate('/admin', { replace: true })
  }

  const steps: SetupWizardStep[] = useMemo(
    () => [
      {
        id: 'welcome',
        label: 'Welcome',
        render: ({ next }) => <Welcome client={client} onNext={next} />,
      },
      {
        id: 'admin-account',
        label: 'Admin account',
        render: ({ next, back }) => (
          <AdminAccount client={client} onNext={next} onBack={back} />
        ),
      },
      {
        id: 'enroll-mfa',
        label: 'Enroll MFA',
        render: ({ next, back }) => (
          <EnrollMFA client={client} onNext={next} onBack={back} />
        ),
      },
      {
        id: 'recovery-key',
        label: 'Recovery key',
        render: ({ next, back }) => (
          <RecoveryKey client={client} onNext={next} onBack={back} />
        ),
      },
      {
        id: 'service-defaults',
        label: 'Service defaults',
        render: ({ next, back }) => (
          <ServiceDefaults
            serviceName={serviceName}
            onServiceNameChange={setServiceName}
            onNext={next}
            onBack={back}
          />
        ),
      },
      {
        id: 'finish',
        label: 'Finish',
        render: ({ next, back }) => (
          <Finish
            client={client}
            serviceName={serviceName}
            onComplete={next}
            onBack={back}
          />
        ),
      },
    ],
    [client, serviceName]
  )

  return (
    <WizardPage>
      <SetupWizardShell
        testID="setup-wizard"
        steps={steps}
        onComplete={handleComplete}
      />
    </WizardPage>
  )
}
